import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { createWorkspace, getBillingAccountWorkspaces } from '~/services/organizationService'
import { BetterToast } from '~/utils'
import {
  initWorkspaceStorage,
  selectWorkspace,
  storeWorkspaceSelection,
  Workspace
} from '~/utils/workspaceSelection'
import { useBillingAccounts } from './BillingAccountsContext'
import { useBillingContext } from './BillingContext'
import { useUserContext } from './UserContext'

// Export workspace types for components that need them
export type { Workspace } from '~/utils/workspaceSelection'

interface WorkspaceContextType {
  workspaces: Workspace[]
  currentWorkspace: Workspace | null
  billingAccountId?: number | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  switchToWorkspace: (workspaceId: number) => Promise<void>
  createNewWorkspace: (workspaceName: string) => Promise<void>
  switching: boolean
  creating: boolean
  updateCurrentWorkspaceRuntimeSetting: (update: Partial<Workspace>) => void
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined)

// Export context for use in SuperAdminTaskDisplay bridge
export { WorkspaceContext }

export function useWorkspaceContext() {
  const context = useContext(WorkspaceContext)
  if (context === undefined) {
    throw new Error('useWorkspaceContext must be used within a WorkspaceProvider')
  }
  return context
}

interface WorkspaceProviderProps {
  children: ReactNode
}

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const { isAuthenticated } = useUserContext()
  const { billingInfo, loaded: billingLoaded } = useBillingContext()
  const { selectedBillingAccountId } = useBillingAccounts()
  const navigate = useNavigate()
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [switching, setSwitching] = useState(false)
  const [creating, setCreating] = useState(false)
  const [storageReady, setStorageReady] = useState(false)
  const [workspaceBillingAccountId, setWorkspaceBillingAccountId] = useState<number | null>(null)
  const fetchRequestIdRef = useRef(0)
  const location = useLocation()
  const workspacePathMatch = location.pathname.match(/^\/w\/([^/]+)/)
  const workspaceUuid = workspacePathMatch?.[1]
  const isOnWorkspacePath = workspacePathMatch !== null
  const billingAccountId = billingInfo?.id ?? null

  // Hydrate workspace selection from bridge storage before reading localStorage.
  // This ensures the previous workspace is restored after SWT webview reloads.
  useEffect(() => {
    initWorkspaceStorage().then(() => setStorageReady(true))
  }, [])

  const loadWorkspaces = useCallback(async (id: number, opts?: { silent?: boolean }) => {
    const requestId = ++fetchRequestIdRef.current
    // Silent (background) reloads keep the current content on screen instead of
    // flipping the global loading flag — an in-place refresh shouldn't blank the
    // page. Only the initial load / account switch shows the loading state.
    if (!opts?.silent) setIsLoading(true)
    setError(null)
    try {
      const fetched = await getBillingAccountWorkspaces(id)
      if (requestId !== fetchRequestIdRef.current) return
      setWorkspaces(fetched)
      setWorkspaceBillingAccountId(id)
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) return
      console.error('Failed to fetch workspaces:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch workspaces')
      setWorkspaces([])
      setCurrentWorkspace(null)
      setWorkspaceBillingAccountId(null)
      setIsLoading(false)
    }
  }, [])

  // Fetch is keyed on the primitive billing account id, so unrelated re-renders
  // that hand down a new `billingInfo` object reference (with the same id) do
  // NOT refetch. The effect runs exactly when the billing account changes.
  useEffect(() => {
    if (!isAuthenticated || !billingLoaded || !storageReady) return
    if (billingAccountId === null) return
    loadWorkspaces(billingAccountId)
  }, [billingAccountId, isAuthenticated, billingLoaded, storageReady, loadWorkspaces])

  // Sync `currentWorkspace` to the URL/storage selection. Runs whenever the URL
  // workspace UUID or the loaded workspace list changes — without re-hitting the
  // workspaces API.
  useEffect(() => {
    if (billingAccountId === null) {
      // Nothing to load without a billing account, so this is a settled state,
      // not a pending one. Callers that block on `isLoading` (e.g. the
      // settings.xml lock in `ProtectedRoute`) would otherwise hang forever.
      setIsLoading(false)
      return
    }
    // Skip until the workspace list for this billing account has been loaded.
    if (workspaceBillingAccountId !== billingAccountId) return

    if (workspaces.length === 0) {
      setIsLoading(false)
      return
    }

    const urlWorkspaceUuid =
      workspaceUuid && workspaces.some(workspace => workspace.uuid === workspaceUuid)
        ? workspaceUuid
        : undefined
    const selected = selectWorkspace(billingAccountId, workspaces, urlWorkspaceUuid)

    if (!selected) {
      setIsLoading(false)
      return
    }

    storeWorkspaceSelection(billingAccountId, selected.uuid)

    if (isOnWorkspacePath && workspaceUuid !== selected.uuid) {
      // Stale URL workspace UUID (e.g. user lost access) — redirect to the
      // resolved selection. Only fires while the user is actually on a `/w/...`
      // path; on other authenticated routes we just expose the selection through
      // context without hijacking navigation.
      navigate(`/w/${selected.uuid}`, { replace: true })
    } else {
      setCurrentWorkspace(prev => (prev?.uuid === selected.uuid ? prev : selected))
      setIsLoading(false)
    }
  }, [
    billingAccountId,
    workspaceBillingAccountId,
    workspaces,
    workspaceUuid,
    isOnWorkspacePath,
    navigate
  ])

  const refetch = useCallback(async () => {
    if (billingAccountId === null) return
    // Background refresh: don't blank the current view while re-syncing.
    await loadWorkspaces(billingAccountId, { silent: true })
  }, [billingAccountId, loadWorkspaces])

  const switchToWorkspace = useCallback(
    async (workspaceId: number) => {
      try {
        setSwitching(true)
        const targetWorkspace = workspaces.find(workspace => workspace.id === workspaceId)
        if (!targetWorkspace) {
          throw new Error('Workspace not found')
        }

        // Store workspace selection
        if (billingInfo) {
          storeWorkspaceSelection(billingInfo.id, targetWorkspace.uuid)
        }

        // Update state and navigate
        setCurrentWorkspace(targetWorkspace)
        navigate(`/w/${targetWorkspace.uuid}`, { replace: true })
        BetterToast.success(`Switched to ${targetWorkspace.name}`)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to switch workspace'
        setError(errorMessage)
        BetterToast.error(errorMessage)
        throw err
      } finally {
        setSwitching(false)
      }
    },
    [workspaces, billingInfo, navigate]
  )

  const createNewWorkspace = useCallback(
    async (workspaceName: string) => {
      try {
        setCreating(true)

        if (!billingInfo) {
          throw new Error('No billing account available to create workspace')
        }

        const newWorkspace = await createWorkspace(billingInfo.id, workspaceName)
        await loadWorkspaces(billingInfo.id)

        if (newWorkspace?.uuid) {
          storeWorkspaceSelection(billingInfo.id, newWorkspace.uuid)
          navigate(`/w/${newWorkspace.uuid}`, { replace: true })
          BetterToast.success('Workspace created successfully!')
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create workspace'
        setError(errorMessage)
        BetterToast.error(errorMessage)
        throw err
      } finally {
        setCreating(false)
      }
    },
    [billingInfo, navigate, loadWorkspaces]
  )

  const updateCurrentWorkspaceRuntimeSetting = useCallback(
    (update: Partial<Workspace>) => {
      if (!currentWorkspace) return

      const currentWorkspaceId = currentWorkspace.id

      // Update workspaces array
      setWorkspaces(prev =>
        prev.map(workspace =>
          workspace.id === currentWorkspaceId ? { ...workspace, ...update } : workspace
        )
      )

      // Update current workspace
      setCurrentWorkspace(prev =>
        prev
          ? {
              ...prev,
              ...update
            }
          : null
      )
    },
    [currentWorkspace]
  )

  // While the user's selected billing account differs from the account whose
  // workspaces are currently loaded, treat the context as loading. This hides the
  // transient gap during account switches when `BillingContext.billingInfo` is
  // briefly null (selected id changed but billing fetch hasn't completed yet) —
  // without this, `currentWorkspace`/`workspaces` would read as stale and consumers
  // would flash NoWorkspaceView or "Workspace Not Found" before the new account loads.
  const effectiveIsLoading =
    isLoading ||
    (selectedBillingAccountId !== null && selectedBillingAccountId !== workspaceBillingAccountId)

  const value: WorkspaceContextType = useMemo(
    () => ({
      workspaces,
      currentWorkspace,
      billingAccountId: workspaceBillingAccountId,
      isLoading: effectiveIsLoading,
      error,
      refetch,
      switchToWorkspace,
      createNewWorkspace,
      switching,
      creating,
      updateCurrentWorkspaceRuntimeSetting
    }),
    [
      workspaces,
      currentWorkspace,
      workspaceBillingAccountId,
      effectiveIsLoading,
      error,
      refetch,
      switchToWorkspace,
      createNewWorkspace,
      switching,
      creating,
      updateCurrentWorkspaceRuntimeSetting
    ]
  )

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}
